/**
 * USE_VARIATION_TIMESHEETS - Timesheet/Allocation Management Hook
 * 
 * SUMMARY:
 * Manages all timesheet and resource allocation functionality for variations.
 * Handles user assignments, allocation CRUD operations, and timesheet scheduling.
 * 
 * @param {Object} params - Hook parameters
 * @param {string} params.optionId - Variation ID
 * @param {string} params.projectId - Project ID
 * @param {Object} params.user - Current user object
 * @param {Function} params.getStages - Callback to refresh stages
 * @returns {Object} Timesheet handlers and state
 */

import { useState } from 'react';
import { useDispatch } from 'react-redux';
import idx from 'idx';
import moment from 'moment';
import {
    allocateResource,
    allocateSchedule,
    deleteResource,
    editTimesheet,
    getResourceAllocations
} from '../../../../actions/timesheet.action';
import { getUserList } from '../../../../actions/user-management.action';
import { warningSnackBar } from '../../../../actions/common.action';

export const useVariationTimesheets = ({
    optionId,
    projectId,
    user,
    getStages
}) => {
    const dispatch = useDispatch();

    // Allocation state
    const [allocationState, setAllocationState] = useState({
        allocates: [],
        allocationId: null,
        allocationDate: null,
        editTimesheet: false,
        allocationType: "",
        userId: user.userData.global_user ? "" : user.userData.id,
        usersList: [],
        showAllocationDialog: false,
        confirmDialogState: false,
        confirmData: null,
        deleteDialogState: false,
        deleteEvent: {},
    });

    /**
     * Get users list for timesheet allocation
     */
    const getUsersList = async () => {
        const status = "ACTIVE";
        const list = await dispatch(getUserList({ status }));
        const usersList = (Array.isArray(list) && list.map(({ id, firstName, lastName }) => ({
            key: (firstName === null ? "" : firstName) + " " + (lastName === null ? "" : lastName),
            value: id,
        }))) || [];
        setAllocationState(prev => ({
            ...prev,
            usersList
        }));
    };

    /**
     * Get allocation list for timesheets
     */
    const getAllocationList = () => {
        if (!optionId) return;

        const allocates = [];
        dispatch(getResourceAllocations({
            taskId: optionId,
            projectId: projectId,
        }))
            .then((res) => {
                const all = JSON.parse(JSON.stringify(res));
                all.forEach((allocate) => {
                    // Prefer the work-hour engine's per-period breakdown (Standard vs Overtime,
                    // no OT1/OT2 tiers) — the legacy overtime_1/overtime_2 columns are never
                    // populated by the engine and only apply to entries that predate it.
                    const timePeriods = allocate.work_hour_breakdown?.time_periods;
                    let normalHours, overtimeHours;
                    if (Array.isArray(timePeriods) && timePeriods.length > 0) {
                        normalHours = timePeriods
                            .filter(p => p.time_type === 'STANDARD')
                            .reduce((sum, p) => sum + (parseFloat(p.hours) || 0), 0);
                        overtimeHours = timePeriods
                            .filter(p => p.time_type !== 'STANDARD')
                            .reduce((sum, p) => sum + (parseFloat(p.hours) || 0), 0);
                    } else {
                        overtimeHours = (allocate.overtime_1 || 0) + (allocate.overtime_2 || 0);
                        normalHours = moment(allocate.endTime).diff(moment(allocate.startTime), 'hours', true) - overtimeHours;
                    }
                    allocates.push({
                        id: allocate.id,
                        userId: allocate.userId,
                        user: mapUserName(allocate.userId),
                        date: moment.utc(allocate.startTime).format("DD/MM/YYYY") + " - " + moment.utc(allocate.endTime).format("DD/MM/YYYY"),
                        starttime: moment.utc(allocate.startTime).format("hh:mm a"),
                        endtime: moment.utc(allocate.endTime).format("hh:mm a"),
                        normalHours,
                        overtimeHours,
                        status: allocate.status,
                    });
                });

                setAllocationState(prev => ({
                    ...prev,
                    allocates,
                }));
            });
    };

    /**
     * Map user ID to user name
     */
    const mapUserName = (userId) => {
        let { usersList } = allocationState;
        let name = '';
        usersList.forEach((user) => {
            if (user.value === userId) {
                name = user.key;
            }
        });
        return name;
    };

    /**
     * Handle allocation dialog open
     */
    const handleAllocationDialog = () => {
        setAllocationState(prev => ({
            ...prev,
            showAllocationDialog: true,
            allocationDate: null,
            allocationId: null,
            editTimesheet: false,
        }));
    };

    /**
     * Close allocation dialog
     */
    const closeAllocationDialog = () => {
        setAllocationState(prev => ({
            ...prev,
            showAllocationDialog: false,
        }));
    };

    /**
     * Handle edit timesheet
     */
    const handleEditTimesheet = (allocationId) => {
        const allocation = allocationState.allocates.find((_) => _.id === allocationId);
        if (!allocation) return;

        setAllocationState(prev => ({
            ...prev,
            allocationType: 'WORK',
            allocationId: allocationId,
            userId: allocation.userId,
            allocationResourceId: allocationId,
            allocationDate: new Date(allocation.starttime),
            showAllocationDialog: true,
            editTimesheet: true,
        }));
    };

    /**
     * Allocate resource handler
     */
    const allocateResourceHandler = async (data) => {
        let { editTimesheet } = allocationState;
        let start = new Date(data.startTime);
        let end = new Date(data.endTime);
        let startTime = new Date(data.startDate);
        let endTime = new Date(data.endDate);
        startTime.setHours(start.getHours(), start.getMinutes(), 0, 0);
        endTime.setHours(end.getHours(), end.getMinutes(), 0, 0);
        startTime = moment.utc(startTime).format("YYYY-MM-DDTHH:mmZ").toString();
        endTime = moment.utc(endTime).format("YYYY-MM-DDTHH:mmZ").toString();
        data["startTime"] = startTime;
        data["endTime"] = endTime;
        delete data.startDate;
        delete data.endDate;
        if (editTimesheet) data.allocationType = "WORK";
        await handleAllocation(data, "WORK");
    };

    /**
     * Handle allocation
     */
    const handleAllocation = async (data, type) => {
        try {
            const { editTimesheet, allocationId } = allocationState;
            let allocationsList = [];

            if (editTimesheet || allocationId) {
                allocationsList = await editTimesheetHandler(data);
            } else {
                allocationsList = await dispatch(allocateResource(data));
            }

            setAllocationState(prev => ({
                ...prev,
                showAllocationDialog: false,
                confirmData: allocationsList,
                confirmDialogState: true,
            }));
        } catch (e) {
            console.error(e);
            setAllocationState(prev => ({
                ...prev,
                showAllocationDialog: true
            }));
        }
    };

    /**
     * Allocate schedule
     */
    const allocateScheduleHandler = async (data) => {
        try {
            await dispatch(allocateSchedule(data));
            getAllocationList();
            setAllocationState(prev => ({
                ...prev,
                confirmDialogState: false,
                showAllocationDialog: false,
            }));
            if (getStages) getStages();
        } catch (e) {
            console.error(e);
            setAllocationState(prev => ({
                ...prev,
                confirmDialogState: false,
                showAllocationDialog: false,
            }));
            getAllocationList();
        }
    };

    /**
     * Close confirm dialog
     */
    const closeConfirmDialog = () => {
        setAllocationState(prev => ({
            ...prev,
            confirmDialogState: false,
        }));
    };

    /**
     * Edit timesheet
     */
    const editTimesheetHandler = async (data) => {
        const { allocationId } = allocationState;
        data.previousType = "WORK";
        data.allocationResourceId = allocationId;
        data.startTime = moment
            .utc(data.startTime)
            .format("YYYY-MM-DDTHH:mmZ")
            .toString();
        data.endTime = moment
            .utc(data.endTime)
            .format("YYYY-MM-DDTHH:mmZ")
            .toString();
        return await dispatch(editTimesheet(allocationId, data));
    };

    /**
     * Delete handler
     */
    const deleteAllocationHandler = async (allocationId) => {
        await dispatch(deleteResource("WORK", allocationId));
        deleteDialogHandler();
        getAllocationList();
    };

    /**
     * Delete dialog handler
     */
    const deleteDialogHandler = (event = {}) => {
        const eventStatus = idx(event, (_) => _.event?.status) || "";
        const isDelete = eventStatus ? eventStatus !== "PENDING" : true;

        if (isDelete) {
            setAllocationState(prev => ({
                ...prev,
                deleteDialogState: !prev.deleteDialogState,
                deleteEvent: event,
                showAllocationDialog: false,
                allocationId: event.id,
            }));
        } else {
            dispatch(warningSnackBar(`This allocation is already ${eventStatus}`));
        }
    };

    return {
        // State
        allocationState,

        // Handlers
        getUsersList,
        getAllocationList,
        mapUserName,
        handleAllocationDialog,
        closeAllocationDialog,
        handleEditTimesheet,
        allocateResourceHandler,
        allocateScheduleHandler,
        closeConfirmDialog,
        deleteAllocationHandler,
        deleteDialogHandler,
    };
};

